import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Calculator, ArrowRight, Check, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { API, formatPrice } from '@/lib/utils';
import { trackLead } from '@/lib/track';
import { getAttribution } from '@/lib/attribution';

const QUOTE_STEPS = [
  {
    id: 'welcome',
    type: 'message',
    message: "Hi! I'm the Legend Quote Assistant. I'll help you tell us about your custom van needs in just 2 minutes. Ready to start?",
    options: [
      { label: "Let's do it!", value: 'start', next: 'usage' },
      { label: "Tell me more first", value: 'info', next: 'info' }
    ]
  },
  {
    id: 'info',
    type: 'message',
    message: "I'll ask you a few quick questions about your needs: how you'll use the van, passenger capacity, must-have features, and your timeline. Our team will then contact you with personalized pricing. Your information stays private and there's no obligation.",
    options: [
      { label: "Sounds good, let's start!", value: 'start', next: 'usage' }
    ]
  },
  {
    id: 'usage',
    type: 'question',
    message: "Great! First, how do you plan to use your custom van?",
    options: [
      { label: "Executive/Business Travel", value: 'executive', priceModifier: 1.3, next: 'passengers' },
      { label: "Family Road Trips", value: 'family', priceModifier: 1.0, next: 'passengers' },
      { label: "Shuttle/Livery Service", value: 'shuttle', priceModifier: 0.8, next: 'passengers' },
      { label: "Mobile Office", value: 'office', priceModifier: 1.2, next: 'passengers' },
      { label: "Luxury Personal Use", value: 'luxury', priceModifier: 1.4, next: 'passengers' }
    ]
  },
  {
    id: 'passengers',
    type: 'question',
    message: "How many passengers do you typically need to accommodate?",
    options: [
      { label: "2-4 passengers", value: '2-4', priceModifier: 1.0, next: 'features' },
      { label: "5-7 passengers", value: '5-7', priceModifier: 1.1, next: 'features' },
      { label: "8-10 passengers", value: '8-10', priceModifier: 1.2, next: 'features' },
      { label: "11+ passengers", value: '11+', priceModifier: 1.0, next: 'features' }
    ]
  },
  {
    id: 'features',
    type: 'multiselect',
    message: "Which features are most important to you? (Select all that apply)",
    options: [
      { label: "Entertainment System (TV, Sound)", value: 'entertainment', price: 2500 },
      { label: "Power Reclining Seats", value: 'power-seats', price: 4500 },
      { label: "Privacy Partition", value: 'partition', price: 1500 },
      { label: "Bar/Refrigerator Area", value: 'bar', price: 3500 },
      { label: "Bathroom/Vanity", value: 'bathroom', price: 6000 },
      { label: "Premium Sound Upgrade", value: 'sound', price: 2100 },
      { label: "Starlink/WiFi", value: 'wifi', price: 1500 },
      { label: "Custom Exterior (Wheels, Paint)", value: 'exterior', price: 5000 }
    ],
    next: 'timeline'
  },
  {
    id: 'timeline',
    type: 'question',
    message: "When are you looking to have your van completed?",
    options: [
      { label: "As soon as possible", value: 'asap', next: 'contact' },
      { label: "Within 3 months", value: '3months', next: 'contact' },
      { label: "Within 6 months", value: '6months', next: 'contact' },
      { label: "Just exploring options", value: 'exploring', next: 'contact' }
    ]
  },
  {
    id: 'contact',
    type: 'contact',
    message: "Almost done! Enter your details below and our team will contact you with personalized pricing for your custom build.",
    next: 'result'
  },
  {
    id: 'result',
    type: 'result',
    message: "Thank you! Your custom van inquiry has been submitted. Our team will contact you with personalized pricing."
  }
];

const BASE_PRICES = {
  'executive': 125000,
  'family': 89000,
  'shuttle': 65000,
  'office': 110000,
  'luxury': 145000
};

const InstantQuoteBot = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Listen for FAB menu event
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('open-quote', handler);
    return () => window.removeEventListener('open-quote', handler);
  }, []);
  const [currentStep, setCurrentStep] = useState('welcome');
  const [messages, setMessages] = useState([]);
  const [answers, setAnswers] = useState({});
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [contact, setContact] = useState({ name: '', email: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeStep = QUOTE_STEPS.find(s => s.id === 'welcome');
      addBotMessage(welcomeStep.message, welcomeStep.options);
    }
  }, [isOpen, messages.length]);

  const addBotMessage = (text, options = null, isResult = false) => {
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'bot',
      text,
      options,
      isResult
    }]);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'user',
      text
    }]);
  };

  const calculateEstimate = () => {
    const usage = answers.usage || 'family';
    let basePrice = BASE_PRICES[usage] || 89000;
    
    // Apply passenger modifier
    const passengerModifier = QUOTE_STEPS.find(s => s.id === 'passengers')
      ?.options.find(o => o.value === answers.passengers)?.priceModifier || 1.0;
    basePrice *= passengerModifier;
    
    // Add feature prices
    let featureTotal = 0;
    const featuresStep = QUOTE_STEPS.find(s => s.id === 'features');
    selectedFeatures.forEach(featureValue => {
      const feature = featuresStep.options.find(o => o.value === featureValue);
      if (feature) featureTotal += feature.price;
    });
    
    const lowEstimate = Math.round((basePrice + featureTotal) * 0.9);
    const highEstimate = Math.round((basePrice + featureTotal) * 1.15);
    
    return { lowEstimate, highEstimate, featureTotal };
  };

  const handleOptionSelect = async (option, stepId) => {
    addUserMessage(option.label);
    
    // Store answer
    setAnswers(prev => ({ ...prev, [stepId]: option.value }));
    
    // Small delay for natural feel
    await new Promise(r => setTimeout(r, 500));
    
    // Move to next step
    if (option.next) {
      const nextStep = QUOTE_STEPS.find(s => s.id === option.next);
      if (nextStep) {
        setCurrentStep(nextStep.id);
        
        if (nextStep.type === 'contact') {
          addBotMessage(nextStep.message);
        } else if (nextStep.type === 'result') {
          // Calculate and show result
          const estimate = calculateEstimate();
          setQuoteResult(estimate);
          addBotMessage(nextStep.message, null, true);
        } else {
          addBotMessage(nextStep.message, nextStep.options);
        }
      }
    }
  };

  const handleFeatureToggle = (featureValue) => {
    setSelectedFeatures(prev => 
      prev.includes(featureValue)
        ? prev.filter(f => f !== featureValue)
        : [...prev, featureValue]
    );
  };

  const handleFeaturesContinue = async () => {
    const featuresStep = QUOTE_STEPS.find(s => s.id === 'features');
    const selectedLabels = selectedFeatures.map(v => 
      featuresStep.options.find(o => o.value === v)?.label
    ).filter(Boolean);
    
    addUserMessage(selectedLabels.length > 0 ? selectedLabels.join(', ') : 'No specific features');
    
    await new Promise(r => setTimeout(r, 500));
    
    const nextStep = QUOTE_STEPS.find(s => s.id === featuresStep.next);
    setCurrentStep(nextStep.id);
    addBotMessage(nextStep.message, nextStep.options);
  };

  const handleContactSubmit = async () => {
    if (!contact.name || !contact.email) {
      toast.error('Please enter your name and email');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const estimate = { submitted: true };
      
      // Save as lead
      const { data: createdLead } = await axios.post(`${API}/leads`, {
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        message: `[Instant Quote Bot] Usage: ${answers.usage}, Passengers: ${answers.passengers}, Features: ${selectedFeatures.join(', ')}, Timeline: ${answers.timeline}`,
        lead_type: 'instant_quote',
        source_page: 'instant_quote_bot',
        attribution: getAttribution()
      });
      trackLead('instant_quote', createdLead?.id, { form_position: 'instant_quote_bot' });

      addUserMessage(`${contact.name} - ${contact.email}`);
      
      await new Promise(r => setTimeout(r, 500));
      
      setQuoteResult(estimate);
      setCurrentStep('result');
      
      const resultStep = QUOTE_STEPS.find(s => s.id === 'result');
      addBotMessage(resultStep.message, null, true);
      
      toast.success('Inquiry submitted! Our team will contact you shortly.');
    } catch (error) {
      console.error('Error saving quote:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetQuote = () => {
    setMessages([]);
    setCurrentStep('welcome');
    setAnswers({});
    setSelectedFeatures([]);
    setContact({ name: '', email: '', phone: '' });
    setQuoteResult(null);
    
    // Restart
    const welcomeStep = QUOTE_STEPS.find(s => s.id === 'welcome');
    setTimeout(() => {
      addBotMessage(welcomeStep.message, welcomeStep.options);
    }, 100);
  };

  const currentStepData = QUOTE_STEPS.find(s => s.id === currentStep);

  if (!isOpen) {
    return (
      <button
        data-testid="instant-quote-toggle"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-6 z-50 bg-navy hover:bg-navy/90 text-white px-6 py-3 rounded-full shadow-xl hidden md:flex items-center gap-2 transition-all hover:scale-105 group"
      >
        <Calculator className="w-5 h-5 text-gold" />
        <span className="font-medium">Instant Quote</span>
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-gold rounded-full animate-ping" />
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-gold rounded-full" />
      </button>
    );
  }

  return (
    <div
      data-testid="instant-quote-bot"
      className="fixed bottom-24 right-6 z-50 w-[400px] max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      style={{ maxHeight: 'calc(100vh - 140px)' }}
    >
      {/* Header */}
      <div className="bg-navy text-white px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center">
            <Calculator className="w-5 h-5 text-navy" />
          </div>
          <div>
            <h4 className="font-semibold text-sm">Instant Quote</h4>
            <p className="text-xs text-white/70">Get your estimate in 2 minutes</p>
          </div>
        </div>
        <button
          data-testid="instant-quote-close"
          onClick={() => setIsOpen(false)}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.map((msg) => (
          <div key={msg.id}>
            {msg.type === 'bot' ? (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <div className="w-8 h-8 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
                    <Calculator className="w-4 h-4 text-gold" />
                  </div>
                  <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm max-w-[85%]">
                    <p className="text-sm text-navy">{msg.text}</p>
                  </div>
                </div>
                
                {/* Result Display */}
                {msg.isResult && quoteResult && (
                  <Card className="ml-10 border-0 shadow-md bg-gradient-to-br from-navy to-navy/90 text-white">
                    <CardContent className="p-5">
                      <p className="text-white/70 text-sm mb-2">Your Inquiry Has Been Submitted</p>
                      <p className="font-display text-xl font-bold text-gold mb-4">
                        We&apos;ll contact you with personalized pricing!
                      </p>
                      <p className="text-xs text-white/60 mb-4">
                        Call us anytime at <a href="tel:6823691500" className="text-gold hover:underline">(682) 369-1500</a>
                      </p>
                      <div className="space-y-2 pt-4 border-t border-white/20">
                        <a href="/build" className="block">
                          <Button className="w-full bg-gold text-navy hover:bg-gold-hover">
                            Build Your Van
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </a>
                        <Button 
                          variant="outline" 
                          className="w-full border-white/30 text-white hover:bg-white/10"
                          onClick={resetQuote}
                        >
                          <RotateCcw className="mr-2 w-4 h-4" />
                          Start New Inquiry
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Options */}
                {msg.options && !msg.isResult && currentStep !== 'features' && currentStep !== 'contact' && (
                  <div className="ml-10 flex flex-wrap gap-2">
                    {msg.options.map((option, idx) => (
                      <button
                        key={idx}
                        data-testid={`quote-option-${option.value}`}
                        onClick={() => handleOptionSelect(option, currentStep)}
                        className="bg-white border border-gray-200 hover:border-gold hover:bg-gold/5 text-navy text-sm px-4 py-2 rounded-full transition-colors"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex justify-end">
                <div className="bg-gold text-navy rounded-2xl rounded-tr-sm px-4 py-2 max-w-[85%]">
                  <p className="text-sm font-medium">{msg.text}</p>
                </div>
              </div>
            )}
          </div>
        ))}
        
        {/* Feature Selection */}
        {currentStep === 'features' && (
          <div className="ml-10 space-y-2">
            {currentStepData?.options.map((option) => (
              <button
                key={option.value}
                data-testid={`feature-${option.value}`}
                onClick={() => handleFeatureToggle(option.value)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-colors text-left ${
                  selectedFeatures.includes(option.value)
                    ? 'border-gold bg-gold/10 text-navy'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <span className="text-sm">{option.label}</span>
                <div className="flex items-center gap-2">
                  {selectedFeatures.includes(option.value) && (
                    <Check className="w-4 h-4 text-gold" />
                  )}
                </div>
              </button>
            ))}
            <Button
              data-testid="features-continue"
              onClick={handleFeaturesContinue}
              className="w-full bg-navy hover:bg-navy/90 mt-2"
            >
              Continue
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        )}
        
        {/* Contact Form */}
        {currentStep === 'contact' && !quoteResult && (
          <div className="ml-10 bg-white rounded-xl p-4 shadow-sm space-y-3">
            <Input
              data-testid="quote-name"
              placeholder="Your Name *"
              value={contact.name}
              onChange={(e) => setContact(prev => ({ ...prev, name: e.target.value }))}
            />
            <Input
              data-testid="quote-email"
              type="email"
              placeholder="Email Address *"
              value={contact.email}
              onChange={(e) => setContact(prev => ({ ...prev, email: e.target.value }))}
            />
            <Input
              data-testid="quote-phone"
              type="tel"
              placeholder="Phone (optional)"
              value={contact.phone}
              onChange={(e) => setContact(prev => ({ ...prev, phone: e.target.value }))}
            />
            <Button
              data-testid="quote-submit"
              onClick={handleContactSubmit}
              disabled={isSubmitting}
              className="w-full bg-gold text-navy hover:bg-gold-hover"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
              <Calculator className="ml-2 w-4 h-4" />
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              No spam, ever. Your info stays private.
            </p>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-white border-t">
        <p className="text-xs text-center text-muted-foreground">
          Contact us for personalized pricing: (682) 369-1500
        </p>
      </div>
    </div>
  );
};

export default InstantQuoteBot;
